import { Theme } from '../stores/useThemeStore';

interface ThemeColors {
    primary: string;
    secondary: string;
    card: string;
    cardAlt: string;
    carousel: string;
    textPrimary: string;
    textSecondary: string;
    accentPrimary: string;
    accentSecondary: string;
    accentPrimaryLight: string;
    borderColor: string;
    borderColorLight: string;
}

interface ThemeSvgFilters {
    primary: string;
}

interface ThemeConfig {
    colors: ThemeColors;
    svgFilters: ThemeSvgFilters;
    fonts: {
        primary: string;
        secondary: string;
    };
    spacing: {
        xs: string;
        sm: string;
        md: string;
        lg: string;
        xl: string;
        xxl: string;
    };
    borderRadius: {
        sm: string;
        md: string;
        lg: string;
        xl: string;
        round: string;
    };
    shadows: {
        sm: string;
        md: string;
        lg: string;
        xl: string;
    };
    transitions: {
        fast: string;
        normal: string;
        slow: string;
    };
}

// Identical in both themes; only colors, filters, and shadows differ.
const SHARED_FONTS: ThemeConfig['fonts'] = {
    primary: 'CustomFont, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
};

const SHARED_SPACING: ThemeConfig['spacing'] = {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
};

const SHARED_BORDER_RADIUS: ThemeConfig['borderRadius'] = {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '24px',
    round: '50%',
};

const SHARED_TRANSITIONS: ThemeConfig['transitions'] = {
    fast: '0.15s ease',
    normal: '0.3s ease',
    slow: '0.5s ease',
};

const darkTheme: ThemeConfig = {
    colors: {
        primary: '#0a0a12',
        secondary: '#121218',
        card: '#18181e',
        cardAlt: '#202028',
        carousel: '#252530',
        textPrimary: '#ffffff',
        textSecondary: '#c0b8ff',
        accentPrimary: '#8e6bff',
        accentSecondary: '#a67fff',
        accentPrimaryLight: 'rgba(142, 107, 255, 0.1)',
        borderColor: '#2d2d40',
        borderColorLight: '#3a3a50',
    },
    svgFilters: {
        primary: 'invert(36%) sepia(100%) saturate(2027%) hue-rotate(242deg) brightness(101%) contrast(101%)',
    },
    fonts: SHARED_FONTS,
    spacing: SHARED_SPACING,
    borderRadius: SHARED_BORDER_RADIUS,
    shadows: {
        sm: '0 2px 4px rgba(0, 0, 0, 0.1)',
        md: '0 4px 8px rgba(0, 0, 0, 0.15)',
        lg: '0 8px 16px rgba(0, 0, 0, 0.2)',
        xl: '0 16px 32px rgba(0, 0, 0, 0.25)',
    },
    transitions: SHARED_TRANSITIONS,
};

const lightTheme: ThemeConfig = {
    colors: {
        primary: 'beige',
        secondary: '#fff8dc',
        card: 'beige',
        cardAlt: '#fff8dc',
        carousel: '#fff8dc',
        textPrimary: '#3e2723',
        textSecondary: '#5d4037',
        accentPrimary: '#8b0000',
        accentSecondary: '#CD5C5C',
        accentPrimaryLight: 'rgba(139, 0, 0, 0.1)',
        borderColor: 'tan',
        borderColorLight: '#deb887',
    },
    svgFilters: {
        primary: 'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
    },
    fonts: SHARED_FONTS,
    spacing: SHARED_SPACING,
    borderRadius: SHARED_BORDER_RADIUS,
    shadows: {
        sm: '0 2px 4px rgba(0, 0, 0, 0.05)',
        md: '0 4px 8px rgba(0, 0, 0, 0.1)',
        lg: '0 8px 16px rgba(0, 0, 0, 0.15)',
        xl: '0 16px 32px rgba(0, 0, 0, 0.2)',
    },
    transitions: SHARED_TRANSITIONS,
};

const themes: Record<Theme, ThemeConfig> = {
    dark: darkTheme,
    light: lightTheme,
};

const CSS_VARIABLES = {
    // Colors
    '--bg-primary': 'colors.primary',
    '--bg-secondary': 'colors.secondary',
    '--bg-card': 'colors.card',
    '--bg-card-alt': 'colors.cardAlt',
    '--bg-carousel': 'colors.carousel',
    '--text-primary': 'colors.textPrimary',
    '--text-secondary': 'colors.textSecondary',
    '--accent-primary': 'colors.accentPrimary',
    '--accent-secondary': 'colors.accentSecondary',
    '--accent-primary-light': 'colors.accentPrimaryLight',
    '--border-color': 'colors.borderColor',
    '--border-color-light': 'colors.borderColorLight',

    // Fonts
    '--font-primary': 'fonts.primary',
    '--font-secondary': 'fonts.secondary',

    // Spacing
    '--spacing-xs': 'spacing.xs',
    '--spacing-sm': 'spacing.sm',
    '--spacing-md': 'spacing.md',
    '--spacing-lg': 'spacing.lg',
    '--spacing-xl': 'spacing.xl',
    '--spacing-xxl': 'spacing.xxl',

    // Border radius
    '--radius-sm': 'borderRadius.sm',
    '--radius-md': 'borderRadius.md',
    '--radius-lg': 'borderRadius.lg',
    '--radius-xl': 'borderRadius.xl',
    '--radius-round': 'borderRadius.round',

    // Transitions
    '--transition-fast': 'transitions.fast',
    '--transition-normal': 'transitions.normal',
    '--transition-slow': 'transitions.slow',

    // SVG Filters
    '--svg-filter': 'svgFilters.primary',
} as const;

const getThemeValue = (theme: ThemeConfig, path: string): string => {
    const keys = path.split('.');
    let value: any = theme;

    for (const key of keys) {
        value = value?.[key];
        if (value === undefined) {
            console.warn(`Theme value not found for path: ${path}`);
            return '';
        }
    }

    return value;
};

export const generateCSSCustomProperties = (theme: ThemeConfig): Record<string, string> => {
    const properties: Record<string, string> = {};

    Object.entries(CSS_VARIABLES).forEach(([cssVar, themePath]) => {
        properties[cssVar] = getThemeValue(theme, themePath);
    });

    return properties;
};

export const isValidTheme = (theme: string): theme is Theme => {
    return theme in themes;
};

export const getThemeConfig = (theme: Theme): ThemeConfig => {
    return themes[theme];
};

export const BREAKPOINTS = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;
